import React, { FC, JSX } from 'react';
import { Link } from 'react-router-dom';
import Button from './Button';
import { Job, JobCardProps } from '../../types/components.d';

const JobCard: FC<JobCardProps> = ({ job, onApply, showApplyButton = true }): JSX.Element => {
  const {
    id,
    title,
    company,
    location,
    type,
    salary,
    postedDate,
    description,
    requirements,
    benefits,
    isRemote = false,
    isUrgent = false,
    experience,
    skills = []
  } = job;

  const formatSalary = (salary: Job['salary']): string => {
    if (!salary) return 'Salary not specified';
    if (typeof salary === 'string') return salary;
    if (typeof salary === 'object' && salary.min && salary.max) {
      return `₹${salary.min.toLocaleString()} - ₹${salary.max.toLocaleString()}`;
    }
    return `N/A`;
  };

  const formatDate = (date: string): string => {
    if (!date) return '';
    const jobDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - jobDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  return (
    <div className="job-card group">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-bold text-primary-black group-hover:text-primary-lightGreen transition-colors duration-300">
              {title}
            </h3>
            {isUrgent && (
              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                Urgent
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="font-semibold text-primary-black">{company}</span>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm text-gray-500 mb-1">{formatDate(postedDate)}</div>
          <div className="text-lg font-bold text-primary-lightGreen">{formatSalary(salary)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <span className="px-3 py-1 bg-primary-teaGreen text-primary-black rounded-full text-sm font-medium">
          {type}
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
          {location}
        </span>
        {isRemote && (
          <span className="px-3 py-1 bg-primary-lightGreen/20 text-primary-lightGreen rounded-full text-sm font-medium">
            Remote
          </span>
        )}
        {experience && (
          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
            {experience}
          </span>
        )}
      </div>

      {description && (
        <p className="text-gray-600 mb-4 line-clamp-3">
          {description}
        </p>
      )}

      {skills && skills.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {skills.slice(0, 5).map((skill, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium"
              >
                {skill}
              </span>
            ))}
            {skills.length > 5 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium">
                +{skills.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <Link
            to={`/jobs/${id}`}
            className="text-primary-lightGreen hover:text-primary-black font-medium transition-colors duration-300"
          >
            View Details
          </Link>
          <Link
            to={`/companies/${company.toLowerCase().replace(/\s+/g, '-')}`}
            className="text-gray-500 hover:text-primary-lightGreen transition-colors duration-300"
          >
            Company Profile
          </Link>
        </div>
        
        {showApplyButton && (
          <Button
            size="sm"
            onClick={() => onApply && onApply(job)}
            className="group-hover:shadow-glow"
          >
            Apply Now
          </Button>
        )}
      </div>
    </div>
  );
};

export default JobCard;
